import { useLazyQuery } from '@apollo/client';
import { Card, DatePicker } from 'antd';
import dayjs from 'dayjs';
import { debounce } from 'lodash';
import React, { useEffect } from 'react';
import {
  BACKEND_MONTH_FORMAT,
  STATISTICS_TABLE_FIELDS,
} from '../../common/constants';
import CommonTable from '../../components/CommonTable';
import SearchComponent from '../../components/SearchComponent';
import {
  antdSortOrder,
  useTableQueryParams,
} from '../../hooks/useTableQueryParams';
import { MONTHLY_STATISTICS } from './graphql/Queries';

const CURRENT_MONTH = dayjs()?.format(BACKEND_MONTH_FORMAT);

const buildStatisticsFilter = (params) => ({
  skip: 0,
  searchString: params?.search,
  month: params?.month,
  sortField: params?.sortField,
  sortOrder: params?.sortOrder,
});

function Statistics() {
  const { params, setParams } = useTableQueryParams({
    fields: STATISTICS_TABLE_FIELDS,
  });

  const [executeQuery, { loading, data }] = useLazyQuery(MONTHLY_STATISTICS, {
    fetchPolicy: 'network-only',
    onError() {},
  });

  const dataSource = data?.monthlyStatistics?.data;

  useEffect(() => {
    executeQuery({ variables: { filters: buildStatisticsFilter(params) } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const handleMonth = (value) => {
    setParams({
      month: value
        ? dayjs(value)?.format(BACKEND_MONTH_FORMAT)
        : CURRENT_MONTH,
    });
  };

  const handleSearch = debounce((value) => {
    setParams({ search: value?.trim() });
  }, 700);

  const onChange = (_, __, sorter) => {
    setParams({
      sortField: sorter?.field,
      sortOrder: sorter?.order === 'descend' ? 'DESC' : 'ASC',
    });
  };

  const columns = [
    {
      title: 'Employee Details',
      dataIndex: 'displayName',
      key: 'displayName',
      sorter: true,
      sortOrder: antdSortOrder(params, 'displayName'),
      render: (value, record) => (
        <>
          {record?.displayName}
          <p className="name-column">
            [{`${record?.department?.name}-${record?.level?.name}`} ]
          </p>
        </>
      ),
    },
    {
      title: 'WFH',
      dataIndex: 'wfhCount',
      key: 'wfhCount',
      align: 'center',
      width: '15%',
      sorter: true,
      sortOrder: antdSortOrder(params, 'wfhCount'),
      render: (value, record) => {
        const wfhCountClass =
          record?.wfhCount > record?.totalWorkFromHome
            ? 'statistics-cell-background'
            : '';
        return (
          <div className="statistics-cell">
            <div className="actual-record">
              {record?.wfhCount}/{record?.totalWorkFromHome || 0}
            </div>
            <div className={wfhCountClass} />
          </div>
        );
      },
    },
    {
      title: 'Late Coming',
      dataIndex: 'lateComingCount',
      key: 'lateComingCount',
      align: 'center',
      width: '15%',
      sorter: true,
      sortOrder: antdSortOrder(params, 'lateComingCount'),
      render: (value, record) => {
        const lateComingClass =
          record?.lateComingCount > record?.totalLateComing
            ? 'statistics-cell-background'
            : '';
        return (
          <div className="statistics-cell">
            <div className="actual-record">
              {record?.lateComingCount}/{record?.totalLateComing || 0}
            </div>
            <div className={lateComingClass} />
          </div>
        );
      },
    },
    {
      title: 'Hours Adjustment',
      dataIndex: 'usedHourAdjustments',
      key: 'usedHourAdjustments',
      align: 'center',
      width: '15%',
      sorter: true,
      sortOrder: antdSortOrder(params, 'usedHourAdjustments'),
      render: (value, record) => {
        const hourAdjustmentsClass =
          record?.usedHourAdjustments > record?.totalHourAdjustment
            ? 'statistics-cell-background'
            : '';
        return (
          <div className="statistics-cell">
            <div className="actual-record">
              {record?.usedHourAdjustments}/{record?.totalHourAdjustment || 0}
            </div>
            <div className={hourAdjustmentsClass} />
          </div>
        );
      },
    },
    {
      title: 'Early Joining',
      dataIndex: 'totalEarlyJoining',
      key: 'totalEarlyJoining',
      align: 'center',
      width: '15%',
      sorter: true,
      sortOrder: antdSortOrder(params, 'totalEarlyJoining'),
      render: (value) => (
        <div className="statistics-cell">
          <div className="actual-record">{value || 0}</div>
        </div>
      ),
    },
    {
      title: 'Early Leaving',
      dataIndex: 'totalEarlyLeaving',
      key: 'totalEarlyLeaving',
      align: 'center',
      width: '15%',
      sorter: true,
      sortOrder: antdSortOrder(params, 'totalEarlyLeaving'),
      render: (value) => (
        <div className="statistics-cell">
          <div className="actual-record">{value || 0}</div>
        </div>
      ),
    },
    {
      title: 'Incident',
      dataIndex: 'totalIncidentReported',
      key: 'totalIncidentReported',
      align: 'center',
      width: '15%',
      sorter: true,
      sortOrder: antdSortOrder(params, 'totalIncidentReported'),
      render: (value) => (
        <div className="statistics-cell">
          <div className="actual-record">{value || 0}</div>
        </div>
      ),
    },
  ];

  return (
    <div className="table-card-page">
      <Card
        className="ant-body-scroll"
        title="Statistics- WFH/ Late Coming / HoursAdjustment"
        extra={
          <>
            <div className="project-filter">
              <SearchComponent
                key={params?.search || ''}
                defaultValue={params?.search}
                getData={handleSearch}
                name="Name, Department, Designation, Level"
              />
              <DatePicker
                value={dayjs(params?.month, BACKEND_MONTH_FORMAT)}
                format="YYYY-MM"
                className="statistic-month-picker"
                picker="month"
                onChange={handleMonth}
              />
            </div>
          </>
        }
      >
        <div className="card-body-wrapper">
          <CommonTable
            size="small"
            className="statistics-table"
            columns={columns}
            dataSource={dataSource}
            onChange={onChange}
            loadingData={loading}
            rowKey={(obj) => obj?.id}
            bordered
          />
        </div>
      </Card>
    </div>
  );
}

export default Statistics;
